import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../config/supabase';
import { User, Session } from '@supabase/supabase-js';

interface UserProfile {
  id: string;
  email: string;
  full_name: string | null;
  avatar_url: string | null;
  google_id: string | null;
  preferred_distance_unit: 'km' | 'miles';
  preferred_theme: 'light' | 'dark' | 'system';
  created_at: string;
  updated_at: string;
  last_sign_in_at: string | null;
}

interface AuthContextType {
  user: User | null;
  profile: UserProfile | null;
  session: Session | null;
  loading: boolean;
  profileLoading: boolean;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// TEMP: bypass Google OAuth and log into a local demo account
const TEMP_AUTH_KEY = 'tourbud_temp_auth';
const TEMP_USER_ID = '00000000-0000-4000-8000-000000000001';

const createTempAuth = (): { user: User; session: Session; profile: UserProfile } => {
  const now = new Date().toISOString();
  const user = {
    id: TEMP_USER_ID,
    email: 'demo@tourbud.app',
    app_metadata: { provider: 'google', providers: ['google'] },
    user_metadata: {
      full_name: 'Demo User',
      name: 'Demo User',
      avatar_url: null,
      email: 'demo@tourbud.app',
      email_verified: true,
    },
    aud: 'authenticated',
    created_at: now,
    role: 'authenticated',
  } as User;

  const session = {
    access_token: 'temp-access-token',
    refresh_token: 'temp-refresh-token',
    expires_in: 60 * 60 * 24 * 30,
    expires_at: Math.floor(Date.now() / 1000) + 60 * 60 * 24 * 30,
    token_type: 'bearer',
    user,
  } as Session;

  const profile: UserProfile = {
    id: TEMP_USER_ID,
    email: 'demo@tourbud.app',
    full_name: 'Demo User',
    avatar_url: null,
    google_id: 'temp-google-id',
    preferred_distance_unit: 'km',
    preferred_theme: 'system',
    created_at: now,
    updated_at: now,
    last_sign_in_at: now,
  };

  return { user, session, profile };
};

const isTempAuthActive = () => localStorage.getItem(TEMP_AUTH_KEY) === '1';

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);

  // Load cached profile from localStorage
  const loadCachedProfile = (userId: string): UserProfile | null => {
    try {
      const cached = localStorage.getItem(`profile_${userId}`);
      if (cached) {
        const profile = JSON.parse(cached) as UserProfile;
        console.log('[AuthContext] Loaded cached profile:', profile);
        return profile;
      }
    } catch (error) {
      console.error('[AuthContext] Error loading cached profile:', error);
    }
    return null;
  };

  // Save profile to localStorage cache
  const cacheProfile = (userId: string, profile: UserProfile) => {
    try {
      localStorage.setItem(`profile_${userId}`, JSON.stringify(profile));
      console.log('[AuthContext] Cached profile for user:', userId);
    } catch (error) {
      console.error('[AuthContext] Error caching profile:', error);
    }
  };

  // Clear profile cache
  const clearCachedProfile = (userId: string) => {
    try {
      localStorage.removeItem(`profile_${userId}`);
      console.log('[AuthContext] Cleared cached profile for user:', userId);
    } catch (error) {
      console.error('[AuthContext] Error clearing cached profile:', error);
    }
  };

  // Fetch user profile from public.users table with timeout
  const fetchProfile = async (userId: string, timeoutMs: number = 5000) => {
    try {
      console.log('[AuthContext] Fetching profile for user:', userId);
      
      // Create a timeout promise
      const timeoutPromise = new Promise<null>((_, reject) => 
        setTimeout(() => reject(new Error('Profile fetch timeout')), timeoutMs)
      );
      
      // Race the fetch against the timeout
      const fetchPromise = supabase
        .from('users')
        .select('*')
        .eq('id', userId)
        .single();
      
      const { data, error } = await Promise.race([fetchPromise, timeoutPromise]) as any;

      if (error) {
        console.error('[AuthContext] Error fetching profile:', error);
        return null;
      }

      console.log('[AuthContext] Profile fetched successfully:', data);
      
      // Cache the profile
      if (data) {
        cacheProfile(userId, data);
      }
      
      return data as UserProfile;
    } catch (error) {
      console.error('[AuthContext] Exception fetching profile:', error);
      return null;
    }
  };

  // Refresh profile data
  const refreshProfile = async () => {
    if (user) {
      const profileData = await fetchProfile(user.id);
      setProfile(profileData);
      // fetchProfile already caches the data
    }
  };

  // Initialize auth state
  useEffect(() => {
    console.log('[AuthContext] Initializing auth state...');
    
    let mounted = true;
    
    // Get initial session with timeout
    const initializeAuth = async () => {
      try {
        // TEMP: restore local demo session if present
        if (isTempAuthActive()) {
          const temp = createTempAuth();
          console.log('[AuthContext] Restoring TEMP demo session');
          if (!mounted) return;
          setSession(temp.session);
          setUser(temp.user);
          setProfile(temp.profile);
          cacheProfile(temp.user.id, temp.profile);
          setProfileLoading(false);
          setLoading(false);
          return;
        }

        const { data: { session } } = await supabase.auth.getSession();
        console.log('[AuthContext] Got initial session:', session ? 'Yes' : 'No');
        
        if (!mounted) return;
        
        setSession(session);
        setUser(session?.user ?? null);
        
        // If user exists, try to load cached profile first
        if (session?.user) {
          const cachedProfile = loadCachedProfile(session.user.id);
          if (cachedProfile) {
            console.log('[AuthContext] Using cached profile');
            setProfile(cachedProfile);
            setProfileLoading(false);
          } else {
            // No cache, need to wait for fetch
            setProfileLoading(true);
          }
        }
        
        // Set loading to false immediately after getting session
        // Don't block on profile fetch
        setLoading(false);
        console.log('[AuthContext] Set loading to false (non-blocking)');
        
        // Fetch profile in background if user exists (to get fresh data)
        if (session?.user) {
          console.log('[AuthContext] Fetching fresh profile data (background)');
          try {
            const profileData = await fetchProfile(session.user.id);
            if (mounted) {
              console.log('[AuthContext] Profile fetch complete');
              setProfile(profileData);
              setProfileLoading(false);
            }
          } catch (error) {
            console.error('[AuthContext] Profile fetch failed:', error);
            if (mounted) {
              // Only clear profile if we didn't have cached data
              if (!profile) {
                setProfile(null);
              }
              setProfileLoading(false);
            }
          }
        }
      } catch (error) {
        console.error('[AuthContext] Failed to get session:', error);
        if (mounted) {
          setLoading(false);
        }
      }
    };
    
    initializeAuth();

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (_event, session) => {
      console.log('[AuthContext] Auth state changed, event:', _event);
      
      if (!mounted) return;

      // TEMP: ignore Supabase null sessions while demo login is active
      if (isTempAuthActive() && !session) {
        return;
      }
      
      setSession(session);
      setUser(session?.user ?? null);
      
      // If user exists, try to load cached profile first
      if (session?.user) {
        const cachedProfile = loadCachedProfile(session.user.id);
        if (cachedProfile) {
          console.log('[AuthContext] Using cached profile in auth change');
          setProfile(cachedProfile);
          setProfileLoading(false);
        } else {
          setProfileLoading(true);
        }
        
        // Fetch fresh profile in background
        fetchProfile(session.user.id)
          .then((profileData) => {
            if (mounted) {
              setProfile(profileData);
              setProfileLoading(false);
            }
          })
          .catch((error) => {
            console.error('[AuthContext] Profile fetch failed in auth change:', error);
            if (mounted) {
              // Only clear profile if we didn't have cached data
              if (!cachedProfile) {
                setProfile(null);
              }
              setProfileLoading(false);
            }
          });
      } else {
        setProfile(null);
        setProfileLoading(false);
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sign in with Google
  // TEMP: skip real Google OAuth and log into a local demo account
  const signInWithGoogle = async () => {
    try {
      const temp = createTempAuth();
      localStorage.setItem(TEMP_AUTH_KEY, '1');
      cacheProfile(temp.user.id, temp.profile);
      setSession(temp.session);
      setUser(temp.user);
      setProfile(temp.profile);
      setProfileLoading(false);
      setLoading(false);
      console.log('[AuthContext] TEMP Google login → demo account:', temp.user.email);
    } catch (error) {
      console.error('Error signing in with Google:', error);
      throw error;
    }
  };

  // Sign out
  const signOut = async () => {
    try {
      console.log('[AuthContext] Signing out...');
      
      // Clear cached profile if user exists
      if (user?.id) {
        clearCachedProfile(user.id);
      }

      // TEMP: clear demo login flag
      const wasTempAuth = isTempAuthActive();
      localStorage.removeItem(TEMP_AUTH_KEY);
      
      // Clear state immediately
      setUser(null);
      setProfile(null);
      setSession(null);
      
      // Then sign out from Supabase (ignore errors for temp sessions)
      const { error } = await supabase.auth.signOut();
      if (error && !wasTempAuth) {
        console.error('[AuthContext] Error signing out:', error);
        throw error;
      }
      
      console.log('[AuthContext] Sign out successful');
    } catch (error) {
      console.error('[AuthContext] Exception during sign out:', error);
      // Even if there's an error, ensure local state is cleared
      localStorage.removeItem(TEMP_AUTH_KEY);
      setUser(null);
      setProfile(null);
      setSession(null);
      throw error;
    }
  };

  const value = {
    user,
    profile,
    session,
    loading,
    profileLoading,
    signInWithGoogle,
    signOut,
    refreshProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

